import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { UserPlus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useState } from "react";

interface SuggestedUser {
  id: string;
  username: string;
  avatar_url: string | null;
  city: string | null;
  country: string | null;
  is_following: boolean;
  relevance_score: number;
}

interface SuggestedUsersProps {
  currentUserId: string;
}

export const SuggestedUsers = ({
 currentUserId }: SuggestedUsersProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [followingUsers, setFollowingUsers] = useState<Set<string>>(new Set());

  // Fetch suggested users
  const { data: suggestedUsers = [], isLoading } = useQuery({
    queryKey: ["suggested-users", currentUserId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_suggested_users_active", {
        p_user_id: currentUserId,
        p_limit: 15,
      });

      if (error) throw error;
      return (data || []) as SuggestedUser[];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Follow user mutation
  const followMutation = useMutation({
    mutationFn: async (targetUserId: string) => {
      const { error } = await supabase.rpc("follow_user", {
        p_target_user_id: targetUserId,
      });

      if (error) throw error;
    },
    onMutate: async (targetUserId) => {
      // Optimistically update UI
      setFollowingUsers((prev) => new Set(prev).add(targetUserId));
    },
    onSuccess: (_, targetUserId) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ["suggested-users", currentUserId] });
      queryClient.invalidateQueries({ queryKey: ["follows", currentUserId] });
      toast.success(t('notifications.toast.following_user'));
    },
    onError: (error, targetUserId) => {
      // Revert optimistic update
      setFollowingUsers((prev) => {
        const next = new Set(prev);
        next.delete(targetUserId);
        return next;
      });
      toast.error(t('notifications.toast.failed_to_follow_user'));
      console.error("Follow error:", error);
    },
  });

  const handleFollow = (userId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent navigation when clicking follow button
    followMutation.mutate(userId);
  };

  const handleUserClick = (username: string) => {
    navigate(`/${username}`);
  };

  // Helper function for avatar URL - handles URLs that may already have cache buster
  const getAvatarUrl = (avatarUrl: string | null | undefined): string | undefined => {
    if (!avatarUrl) return undefined;
    // If URL already has a timestamp, use it as-is
    if (avatarUrl.includes('?t=') || avatarUrl.includes('&t=')) {
      return avatarUrl;
    }
    const separator = avatarUrl.includes('?') ? '&' : '?';
    return `${avatarUrl}${separator}t=${Date.now()}`;
  };

  if (isLoading) {
    return (
      <div className="mb-6">
        <h3 className="text-sm font-semibold mb-3 text-muted-foreground">{t('ui.text.suggested_users')}</h3>
        <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex-shrink-0">
              <div className="w-16 h-16 rounded-full bg-muted animate-pulse" />
              <div className="w-16 h-3 bg-muted rounded mt-2 animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!suggestedUsers || suggestedUsers.length === 0) {
    return null; // Don't show anything if no suggestions
  }

  return (
    <div className="mb-6">
      <h3 className="text-sm font-semibold mb-3 text-muted-foreground">{t('ui.text.suggested_for_you')}</h3>
      <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide">
        {suggestedUsers.map((user) => {
          const isFollowing = followingUsers.has(user.id);
          return (
            <div
              key={user.id}
              className="flex-shrink-0 cursor-pointer group"
              onClick={() => handleUserClick(user.username)}
            >
              <div className="relative w-16 h-16">
                <Avatar className="w-16 h-16 border-2 border-background group-hover:border-primary transition-colors">
                  <AvatarImage src={getAvatarUrl(user.avatar_url)} />
                  <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                    {user.username?.[0]?.toUpperCase() || "?"}
                  </AvatarFallback>
                </Avatar>
                {!isFollowing && (
                  <Button
                    size="icon"
                    variant="default"
                    className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-6 h-6 rounded-full shadow-lg system:bg-white system:text-black system:hover:bg-white/90 dark:bg-primary dark:text-primary-foreground"
                    onClick={(e) => handleFollow(user.id, e)}
                    disabled={followMutation.isPending}
                  >
                    <UserPlus className="w-3 h-3" />
                  </Button>
                )}
              </div>
              <p className="text-xs text-center mt-2 dark:text-white system:text-white font-medium truncate w-16">
                @{user.username}
              </p>
            </div>
          );
        })}
      </div>
      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
};
